import { useLazyQuery } from '@apollo/client';
import { Empty, Select, Spin } from 'antd';
import { debounce, uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { LIMIT, SORT } from '../../../../../common/constants';
import SelectComponent from '../../../../../components/SelectComponent';
import { PINCODES } from '../graphql/Queries';

const { Option } = Select;

const LibraryPincodeSelect = (props) => {
  const { initialPincodes, isIdAsValue = false, hideAreaName = false, ...rest } = props;
  const [pincodeList, setPincodeList] = useState(initialPincodes || []);
  const [pincodesSearchTerm, setPincodesSearchTerm] = useState('');
  const [isAllPincodesFetched, setIsAllPincodesFetched] = useState(false);
  const [isPincodesAllowClear, setIsPincodesAllowClear] = useState(false);
  const [isPincodesDropdownVisible, setIsPincodesDropdownVisible] = useState(
    false,
  );
  const [isFetchMorePincodesLoading, setIsFetchMorePincodesLoading] = useState(
    false,
  );
  const [isLibrarySelected, setIsLibrarySelected] = useState(false);
  const [isQueryLoading, setIsQueryLoading] = useState(false);

  const [pincodes, { loading: isPincodesLoading }] = useLazyQuery(PINCODES, {
    onCompleted: (response) => {
      const fetched = response?.pincodes?.data || [];
      if (fetched.length < LIMIT) {
        setIsAllPincodesFetched(true);
      }
      setIsFetchMorePincodesLoading(false);
      setIsLibrarySelected(false);
      setIsQueryLoading(false);
      setPincodeList((prevList) =>
        uniqBy(
          [...(initialPincodes || []), ...prevList, ...fetched],
          'id',
        ),
      );
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError() {
      setIsQueryLoading(false);
    },
  });

  useEffect(() => {
    if (!isPincodesDropdownVisible) {
      setPincodesSearchTerm('');
      setPincodeList(initialPincodes || []);
      setIsQueryLoading(true);
      pincodes({
        variables: {
          filter: {
            limit: LIMIT,
            skip: 0,
            search: pincodesSearchTerm,
          },
          sort: {
            field: 'pincode',
            order: SORT?.ASC,
          },
        },
      });
    }
    setIsAllPincodesFetched(false);
  }, [isPincodesDropdownVisible]);

  useEffect(() => {
    if (initialPincodes?.length > 0) {
      setPincodeList((prevList) =>
        uniqBy([...initialPincodes, ...prevList], 'id'),
      );
    }
  }, [initialPincodes]);

  useEffect(() => {
    if (isLibrarySelected && isPincodesDropdownVisible && pincodesSearchTerm) {
      setPincodesSearchTerm('');
      setPincodeList([]);
      setIsQueryLoading(true);
      pincodes({
        variables: {
          filter: {
            limit: LIMIT,
            skip: 0,
          },
          sort: {
            field: 'pincode',
            order: SORT?.ASC,
          },
        },
      });
    }
    setIsAllPincodesFetched(false);
  }, [isLibrarySelected, isPincodesDropdownVisible, pincodesSearchTerm]);

  const handlePincodesScroll = (event) => {
    if (!isAllPincodesFetched && !isPincodesLoading && !isQueryLoading) {
      const target = event?.target;
      const currentLength = pincodeList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        setIsFetchMorePincodesLoading(true);
        setIsQueryLoading(true);
        pincodes({
          variables: {
            filter: {
              limit: LIMIT,
              skip: currentLength,
              search: pincodesSearchTerm,
            },
            sort: {
              field: 'pincode',
              order: SORT?.ASC,
            },
          },
        });
      }
    }
  };

  const handlePincodesSearch = (searchTerm) => {
    const trimSearch = searchTerm?.trim();
    setIsAllPincodesFetched(false);
    setPincodeList([]);
    setPincodesSearchTerm(trimSearch);
    setIsQueryLoading(true);
    pincodes({
      variables: {
        filter: {
          limit: LIMIT,
          skip: 0,
          search: trimSearch,
        },
        sort: {
          field: 'pincode',
          order: SORT?.ASC,
        },
      },
    });
  };

  const handlePincodesScrollDebounce = debounce(handlePincodesScroll, 500);
  const handleSearchPincodesDebounce = debounce(handlePincodesSearch, 500);

  useEffect(() => {
    handlePincodesScrollDebounce?.cancel();
  }, [handlePincodesScrollDebounce]);

  useEffect(() => {
    handleSearchPincodesDebounce?.cancel();
  }, [handleSearchPincodesDebounce]);

  useEffect(() => {
    if (isPincodesLoading) {
      setIsPincodesAllowClear(false);
    } else {
      setIsPincodesAllowClear(true);
    }
  }, [isPincodesLoading]);

  return (
    <SelectComponent
      mode="multiple"
      placeholder="Select Pincodes"
      onDropdownVisibleChange={(e) => setIsPincodesDropdownVisible(e)}
      onPopupScroll={handlePincodesScrollDebounce}
      onSearch={handleSearchPincodesDebounce}
      onChange={() => setIsPincodesAllowClear(true)}
      allowClear={isPincodesAllowClear}
      filterOption={false}
      notFoundContent={
        isPincodesLoading || isQueryLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 0',
            }}
          >
            <Spin size="small" />
          </div>
        ) : (
          <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
        )
      }
      onSelect={() => setIsLibrarySelected(true)}
      maxTagCount={false}
      dropdownRender={(menu) => (
        <>
          {menu}
          {isFetchMorePincodesLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 0',
              }}
            >
              <Spin size="small" />
            </div>
          )}
        </>
      )}
      {...rest}
    >
      {pincodeList?.map((pincode) => (
        <Option
          key={pincode?.id}
          value={isIdAsValue ? pincode?.id : pincode?.pincode}
        >
          {pincode?.pincode}{!hideAreaName ? ` (${pincode?.areaName || '-'})` : ''}
        </Option>
      ))}
    </SelectComponent>
  );
};

export default LibraryPincodeSelect;
